import { type RoutineLibraryItem } from '@littlebird/sdk';
import { useMatchRoute } from '@tanstack/react-router';
import { observer } from 'mobx-react-lite';

import { ModalsManager } from '@/components/modals/modalsManager';
import { Text } from '@/components/ui';
import { YStack } from '@/components/ui/layout/layout';
import { pickRecommendedRoutines } from '@/features/context-collection/first-run-summary.message';
import { LibraryCard } from '@/features/routines/components/routine-library-section';
import { CreateRoutineModal } from '@/features/routines/routines-create';
import { useRoutineLibrary } from '@/features/routines/use-routine-refresh';
import { useRoutinesLimitState } from '@/features/subscription/hooks/use-routines-limit-state';
import { RoutinesLimitWarning } from '@/features/subscription/routines-limits';
import { trackEvent } from '@/utils/analytics';
import { navigate } from '@/utils/navigation-service';

export const RoutineRecommendations = observer(
  ({ slugs }: { slugs: string[] }) => {
    const matchRoute = useMatchRoute();
    const isSharedChat = !!matchRoute({ to: '/shared-chat/$id' });
    const { items: libraryItems } = useRoutineLibrary(!isSharedChat);
    const { hasReachedLimit } = useRoutinesLimitState();

    const items = pickRecommendedRoutines(libraryItems, slugs);

    if (isSharedChat || items.length === 0) return null;

    const onSelect = (item: RoutineLibraryItem) => {
      if (hasReachedLimit) return;
      trackEvent('ROUTINE_LIBRARY_EVENT', {
        actionName: 'first_run_card_clicked',
        templateId: item.id,
        templateTitle: item.title,
      });
      ModalsManager.show(CreateRoutineModal, { libraryItem: item });
    };

    return (
      <YStack className="my-3 gap-3">
        <Text className="text-foreground-muted">
          Want me to keep this going automatically? These routines could help:
        </Text>
        <RoutinesLimitWarning />
        <div className="grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(220px,1fr))]">
          {items.map((item) => (
            <LibraryCard
              key={item.id}
              item={item}
              onSelect={onSelect}
              disabled={hasReachedLimit}
            />
          ))}
        </div>
        <button
          type="button"
          onClick={() => navigate({ to: '/routines' })}
          className="self-start text-sm text-foreground-muted hover:text-foreground"
        >
          See all routines →
        </button>
      </YStack>
    );
  }
);
